import { format } from 'date-fns';
import { EventInfoResponse, timestampToDate } from './api';

export type PlanType = 'Premium' | 'Pro' | 'Free' | 'Inactive';

export interface PhaseConfig {
  // 用户订阅的plan类型：premium (0.5 BNB), pro (1.5 BNB), free
  planType: PlanType;
  // 用户是否已经产生了挖矿收益
  isHavingEarning: boolean;
  // 用户存入的BNB数量
  phase1Deposit: number;  
  // Phase1 开始时间
  phase1StartTime: Date;
  // Phase1 结束时间
  phase1EndTime: Date;
  // Phase2 结束时间
  phase2EndTime: Date;
  // Phase1 分配数量
  phase1Allocation: number;
  // Phase2 分配数量
  phase2Allocation: number;
  // 未完成任务数
  unfinished_tasks: number;
}

// 默认配置，可用于测试
export const defaultPhaseConfig: PhaseConfig = {
  planType: 'Inactive',
  isHavingEarning: false,
  phase1Deposit: 0,
  phase1StartTime: new Date('2025-06-06T18:00:00Z'), // 2025-6-6 6:00 PM UTC
  phase1EndTime: new Date('2025-06-06T20:00:00Z'), // Phase1 结束时间
  phase2EndTime: new Date('2025-09-06T20:00:00Z'), // Phase2 结束时间
  phase1Allocation: 45000000,
  phase2Allocation: 65000000,
  unfinished_tasks: 1,
};

export enum PhaseTimeState {
  BEFORE_PHASE1 = 1, // Phase1未开始
  DURING_PHASE1 = 2, // Phase1进行中
  DURING_PHASE2 = 3, // Phase2进行中
  AFTER_PHASE2 = 4,  // Phase2结束后
}

// 获取当前是哪个时间段
export function getCurrentPhaseTimeState(config: PhaseConfig, currentTime: Date = new Date()): PhaseTimeState {
  if (currentTime <= config.phase1StartTime) {
    return PhaseTimeState.BEFORE_PHASE1;
  } else if (currentTime <= config.phase1EndTime) {
    return PhaseTimeState.DURING_PHASE1;
  } else if (currentTime <= config.phase2EndTime) {
    return PhaseTimeState.DURING_PHASE2;
  } else {
    return PhaseTimeState.AFTER_PHASE2;
  }
}

// 格式化时间显示
export function formatUTCTime(date: Date): string {
  return format(date, "yyyy-MM-dd h:mm a 'UTC'");
}

// 计算倒计时
export function getCountdown(targetDate: Date, currentDate: Date = new Date()): string {
  const diffInMs = targetDate.getTime() - currentDate.getTime();
  
  if (diffInMs <= 0) {
    return "0d:0h:0m";
  }
  
  const days = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
  
  return `${days}d:${hours}h:${minutes}m`;
}

// PIN AI Banner文本内容
export function getPINAIBannerContent(config: PhaseConfig, currentTime: Date = new Date()): {
  text: string;
  className: string;
} {
  const phaseState = getCurrentPhaseTimeState(config, currentTime);
  
  switch (phaseState) {
    case PhaseTimeState.BEFORE_PHASE1:
      return {
        text: `Coming in ${getCountdown(config.phase1StartTime, currentTime)}`,
        className: "bg-accent-yellow", // 默认黄色背景
      };
    case PhaseTimeState.DURING_PHASE1:
    case PhaseTimeState.DURING_PHASE2:
      return {
        text: "LIVE NOW!",
        className: "[background:linear-gradient(79deg,rgba(73,34,134,1)_1%,rgba(128,81,214,1)_96%)] [color:var(--Secondary-Orange,#F9AD58)] text-center font-sf-pro text-lg font-medium leading-[22px]", // 紫色渐变背景，自定义样式
      };
    case PhaseTimeState.AFTER_PHASE2:
      return {
        text: "Sale Finished!",
        className: "bg-[#7a6eaa] text-element-lightwhite text-center font-sf-pro text-lg font-medium leading-[22px]", // 紫色背景，白色文字
      };
  }
}

// AI Credit Sale文本内容
export function getAICreditSaleContent(config: PhaseConfig, currentTime: Date = new Date()): {
  text: string;
  className: string;
} {
  const phaseState = getCurrentPhaseTimeState(config, currentTime);
  
  switch (phaseState) {
    case PhaseTimeState.BEFORE_PHASE1:
      return {
        text: `Starts at ${formatUTCTime(config.phase1StartTime)}`,
        className: "text-accent-orange", // 橙色
      };
    case PhaseTimeState.DURING_PHASE1:
      return {
        text: `● Live now! Ends in ${getCountdown(config.phase1EndTime, currentTime)}`,
        className: "text-[#43C6A7]", // 绿色
      };
    case PhaseTimeState.DURING_PHASE2:
    case PhaseTimeState.AFTER_PHASE2:
      return {
        text: `Sale Finished! Deposited ${config.phase1Deposit} BNB`,
        className: "text-gray-500", // 灰色 + 紫色（需要在组件中处理紫色部分）
      };
  }
}

// AI Mining Machine文本内容
export function getAIMiningMachineContent(config: PhaseConfig, currentTime: Date = new Date()): {
  text: string;
  className: string;
  isPaid: boolean;
  isPaidAndGotScore: boolean;
  isTaskFinished: boolean; 
  unfinished_tasks: number;
} {
  const phaseState = getCurrentPhaseTimeState(config, currentTime);
  
  const isPaid = config.planType !== 'Inactive';
  const isPaidAndGotScore = isPaid && config.isHavingEarning;
  const isTaskFinished = config.unfinished_tasks === 0;

  // 如果isHavingEarning没有产生分数，则显示1个未完成任务
  const unfinished_tasks = !config.isHavingEarning ? 1 : config.unfinished_tasks;
  
  switch (phaseState) {
    case PhaseTimeState.BEFORE_PHASE1:
      return {
        text: `Starts at ${formatUTCTime(config.phase1EndTime)}`,
        className: "text-accent-orange", // 橙色
        isPaidAndGotScore,
        isPaid,
        unfinished_tasks: unfinished_tasks,
        isTaskFinished: isTaskFinished,
      };
    case PhaseTimeState.DURING_PHASE1:
      return {
        text: `Starts in ${getCountdown(config.phase1EndTime, currentTime)}`,
        className: "text-accent-orange", // 橙色
        isPaidAndGotScore,
        isPaid,
        unfinished_tasks: unfinished_tasks,
        isTaskFinished: isTaskFinished,
      };
    case PhaseTimeState.DURING_PHASE2:
      return {
        text: `● Live now! Subscribed ${config.planType === 'Premium' ? 'Premium' : 'Pro'} Plan`,
        className: "text-[#43C6A7]", // 绿色
        isPaidAndGotScore,
        isPaid,
        unfinished_tasks: unfinished_tasks,
        isTaskFinished: isTaskFinished,
      };
    case PhaseTimeState.AFTER_PHASE2:
      return {
        text: `Sale Finished! Subscribed ${config.planType === 'Premium' ? 'Premium' : 'Pro'} Plan`,
        className: "text-gray-500", // 灰色
        isPaidAndGotScore,
        isPaid,
        unfinished_tasks: unfinished_tasks,
        isTaskFinished: isTaskFinished,
      };
  }
}

// 用于颜色高亮的助手组件，可以将指定文本部分高亮为紫色
export function highlightAmountText(text: string): {
  prefix: string;
  highlight: string;
  suffix: string;
} {
  // 根据感叹号(!)分割文本
  // 感叹号及其之前的部分作为prefix
  // 感叹号之后到结尾的部分作为highlight
  // suffix为空字符串
  const exclamationIndex = text.indexOf('!');
  
  if (exclamationIndex !== -1) {
    return {
      prefix: text.substring(0, exclamationIndex + 1), // 包含感叹号
      highlight: text.substring(exclamationIndex + 1), // 感叹号之后的部分
      suffix: '',
    };
  }
  
  // 如果没有找到感叹号，则整个文本作为prefix
  return {
    prefix: text,
    highlight: '',
    suffix: '',
  };
}

// 将API响应转换为PhaseConfig
export function mapEventInfoToPhaseConfig(eventInfo: EventInfoResponse): PhaseConfig {
  // 将plan_type映射到PlanType
  const mapPlanType = (planType: string | null): PlanType => {
    if (!planType) return 'Inactive';
    switch (planType.toLowerCase()) {
      case 'premium':
        return 'Premium';
      case 'pro':
        return 'Pro';
      case 'free':
        return 'Free';
      default:
        return 'Inactive';
    }
  };

  return {
    planType: mapPlanType(eventInfo.plan_type),
    isHavingEarning: eventInfo.has_earnings || false,
    phase1Deposit: eventInfo.phase1_deposit || 0,
    phase1StartTime: timestampToDate(eventInfo.phase1_start_time),
    phase1EndTime: timestampToDate(eventInfo.phase1_end_time),
    phase2EndTime: timestampToDate(eventInfo.phase2_end_time),
    phase1Allocation: eventInfo.phase1_allocation,
    phase2Allocation: eventInfo.phase2_allocation,
    unfinished_tasks: eventInfo.unfinished_tasks,
  };
}
